/**
 * The employee's own experience: who gets it, and what their menu holds
 * (Employee round B1/B3; Employee rebuild C General).
 *
 * WHO. Decided by PERMISSION, never by a role name, so a custom role lands on
 * the same side as the base role it widens. Somebody gets the employee
 * experience when they plan their own office days (hybrid:schedule_submit),
 * see no organisation figures (no analytics:view), and hold none of the
 * front-line staff powers below. Against the backend's role map that is the
 * `employee` role alone: reception holds visitor:checkin, a technician
 * maintenance:view_own, every manager analytics:view — so their home and menu
 * are exactly what they were. employeeMenu.test.ts holds that to every role.
 *
 * Because the test is the permission set, a custom role cut from the
 * employee's set lands here too, and an employee granted helpdesk:view_all
 * (or any power below) does not. This is the ONE helper that decides it.
 *
 * WHAT. Their own shell (Employee rebuild C General): My Day as home, a
 * grouped sidebar (Workspace / Services / footer) and a five-item phone bar.
 * Every other role keeps the shell it had. The employee pages themselves are
 * gated by PERMISSION in routeAccess, so any role holding the employee
 * permissions can still open them by URL — this narrows what is offered, not
 * what is allowed.
 */
import { hasPermFor } from '@/utils/permissions';
import type { UserRole } from '@/types';
import { FM_HOME, isFmExperience } from './fmExperience';

type PermUser = {
  role: UserRole; permissions?: string[] | null; site_scope?: { mode: string } | null;
} | null | undefined;

/** Holding any one of these makes somebody staff for this purpose. */
const STAFF_POWERS = [
  'analytics:view',
  // Employee rebuild F0: reading everybody's tickets, visits or bookings is
  // what makes somebody front-line staff, whatever else their role holds.
  'helpdesk:view_all',
  'visitor:view_all',
  'booking:view_all',
  'visitor:checkin',
  'maintenance:view',      // either half: view_all or view_own
  'maintenance:work',
  'helpdesk:edit',
  'emergency:trigger',
  'asset:view_all',
  'admin:view',
  // Reception rebuild: a lobby desk holds none of the visitor powers above.
  'reception:desk',
] as const;

export function isEmployeeExperience(user: PermUser): boolean {
  if (!user) return false;
  if (!hasPermFor(user, 'hybrid:schedule_submit')) return false;
  return !STAFF_POWERS.some((p) => hasPermFor(user, p));
}

/**
 * Employee R2 (review R2A C General): the Q panel and the keyboard shortcuts
 * name the staff modules (/spaces, /helpdesk, /visitors). For the employee
 * shell the same intent lives on the employee screens — Book, My requests, My
 * visitors, My Day — so this is the ONE place that translates a staff target.
 * Every other role gets the target back untouched.
 */
const EMPLOYEE_TARGETS: Record<string, string> = {
  '/spaces?action=book-desk': '/book?tab=desk',
  '/spaces#room-booking': '/book?tab=room',
  '/helpdesk?action=new': '/my-requests',
  '/visitors?action=new': '/my-visitors?tab=invite',
  '/spaces': '/book',
  '/helpdesk': '/my-requests',
  '/visitors': '/my-visitors',
  '/dashboard': '/my-day',
};

export function employeeTargetFor(user: PermUser, to: string): string {
  return isEmployeeExperience(user) ? (EMPLOYEE_TARGETS[to] ?? to) : to;
}

/** Where the employee experience lands: after sign-in, on `/`, and on
 *  `/dashboard` (Employee rebuild C1 — My Day is its own route, not a page
 *  swapped in under /dashboard as the first cut did). */
export const EMPLOYEE_HOME = '/my-day';

/** Where this person's Home is: My Day for the employee experience, the FM
 *  home for the FM Manager (FM rebuild C0.3). Everybody else keeps /dashboard. */
export function homePathFor(user: PermUser): string {
  // FM Technician C2: a technician with no site yet lands on "No site yet".
  // Reception R2: a receptionist with no building, on the desk's own screen.
  if (user?.site_scope?.mode === 'none') {
    return hasPermFor(user, 'reception:desk') ? '/reception/no-building' : '/tech/no-site';
  }
  if (isFmExperience(user)) return FM_HOME;
  if (hasPermFor(user, 'reception:desk') && !hasPermFor(user, 'admin:view')) return '/reception';
  return isEmployeeExperience(user) ? EMPLOYEE_HOME : '/dashboard';
}

export interface EmployeeNavEntry { label: string; path: string }
export interface EmployeeNavGroup { id: string; heading: string | null; items: EmployeeNavEntry[] }

/**
 * The employee's sidebar (Employee rebuild C General), in the prototype's
 * order: My Day · Workspace · Services. Book parking is no longer an item of
 * its own — parking is a tab of Book (C2).
 *
 * 🔴 EVERY ITEM STILL PASSES canOfferInNav, so a custom role on the employee
 * set without, say, asset:view_own is not offered My equipment only to be
 * told "no access" there.
 */
export const EMPLOYEE_NAV_GROUPS: readonly EmployeeNavGroup[] = [
  { id: 'top', heading: null, items: [{ label: 'My Day', path: '/my-day' }] },
  {
    id: 'workspace', heading: 'Workspace', items: [
      { label: 'Book', path: '/book' },
      { label: 'My bookings', path: '/my-bookings' },
      { label: 'Office days', path: '/office-days' },
      { label: "Who's in", path: '/whos-in' },
      { label: 'My visitors', path: '/my-visitors' },
    ],
  },
  {
    id: 'services', heading: 'Services', items: [
      { label: 'My requests', path: '/my-requests' },
      { label: 'My equipment', path: '/my-equipment' },
      { label: 'Wellbeing', path: '/wellbeing' },
      { label: 'Safety', path: '/my-safety' },
    ],
  },
];

/** Pinned at the foot of the employee's sidebar. */
export const EMPLOYEE_NAV_FOOTER: readonly EmployeeNavEntry[] = [
  { label: 'Notifications', path: '/notifications' },
  { label: 'Settings', path: '/settings' },
];

/** Every route the employee's menu offers — the sidebar's groups and its
 *  footer. canOfferInNav refuses anything else to the employee experience. */
export const EMPLOYEE_NAV: readonly string[] = [
  ...EMPLOYEE_NAV_GROUPS.flatMap((g) => g.items.map((i) => i.path)),
  ...EMPLOYEE_NAV_FOOTER.map((i) => i.path),
];

/** Routes in the general sidebar (Sidebar NAV_ITEMS) offered to the employee
 *  experience and nobody else, so no other role's menu grows an item. They
 *  still open for anyone the route map allows.
 *
 *  🔴 BUG_121 retest (2 Nov 2026): /my-bookings WAS THE ONE ENTRY, AND IT HID
 *  MY BOOKINGS FROM EVERY ADMIN. An org admin or super admin books rooms and
 *  desks from the Spaces page, but their menu had no My bookings, so the
 *  tester filed "MyBooking page missing" and could not reach Book's Back link
 *  at all. It is now offered to everyone who can book (NAV_OFFER in
 *  routeAccess), and this list is empty until something needs it again. */
export const EMPLOYEE_ONLY_NAV: readonly string[] = [];
